import { fetchProjectVersions } from './api.js';
import type { BugSmashConfig, ProjectVersion, ResolvedBugSmashConfig } from './types.js';
import { matchProjectVersion } from './versionMatch.js';

type RuntimeResolvedBugSmashConfig = ResolvedBugSmashConfig & {
  baseAppUrl?: string;
};

function resolveTargetVersion(
  versions: ProjectVersion[],
  config: BugSmashConfig,
): ProjectVersion {
  if (config.version != null) {
    const matched = matchProjectVersion(versions, config.version);
    if (!matched) {
      throw new Error(`[BugSmash] Version not found: ${String(config.version)}`);
    }
    return matched;
  }

  return versions.find((version) => version.isLatest) ?? versions[0];
}

function isPauseCommentEnabled(value: ProjectVersion['pauseComment']): boolean {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return normalized === '1' || normalized === 'true';
  }

  return false;
}

export async function resolveWidgetConfig(
  config: BugSmashConfig,
): Promise<ResolvedBugSmashConfig> {
  const resolved: RuntimeResolvedBugSmashConfig = {
    projectId: config.projectId,
    position: config.position ?? 'bottom-right',
    commentsEnabled: true,
  };
  if (config.baseAppUrl) {
    resolved.baseAppUrl = config.baseAppUrl;
  }

  const versions = await fetchProjectVersions(resolved);
  const targetVersion = resolveTargetVersion(versions, config);

  return {
    ...resolved,
    projectVersionId: targetVersion.id,
    commentsEnabled: !isPauseCommentEnabled(targetVersion.pauseComment),
  };
}
